import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import {
  extractAlternateLinksFromPageData,
  extractPageRecordFromResponse,
  type AlternateLink,
} from "../utils/alternateLinks";
import { resolveAlternateLinksForRoute } from "../utils/discoverAlternateLinks";
import { getPageQueryForRoute } from "../utils/routeQueries";
import { fetchGlobalData } from "../utils/utils";

export function useAlternateLinks(): AlternateLink[] | undefined {
  const location = useLocation();
  const pageQuery = getPageQueryForRoute(location.pathname);

  // Subscribe to the same query key as page components (NewsPage, DynamicPage, etc.)
  // so discovery re-runs after page data lands in the cache.
  const {
    data: pageData,
    isFetched,
    isError,
  } = useQuery({
    queryKey: pageQuery ? [pageQuery] : ["alternateLinks-no-page"],
    queryFn: () => fetchGlobalData(pageQuery!),
    enabled: !!pageQuery,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const linksFromCache = pageData
    ? extractAlternateLinksFromPageData(extractPageRecordFromResponse(pageData))
    : undefined;

  const isPageQuerySettled = isFetched || isError;
  const shouldFetchFallback =
    !!pageQuery && !linksFromCache && isPageQuerySettled;

  const { data: discoveredLinks } = useQuery({
    queryKey: ["alternateLinks-resolve", location.pathname, pageQuery],
    queryFn: () => resolveAlternateLinksForRoute(location.pathname, pageData),
    enabled: shouldFetchFallback,
    staleTime: 5 * 60 * 1000,
  });

  return linksFromCache ?? discoveredLinks;
}
